import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Database, Download, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';

const BackupSettings = () => {
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState({ logs: [], stats: {} });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchStatus(), fetchLogs()]);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await api.get('/backup/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await api.get('/backup/logs?limit=30');
      setLogs(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloading(true);
    try {
      toast.info('Creating backup... Please wait');
      
      const response = await api.get('/backup/download', {
        responseType: 'blob'
      });
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `backup_${new Date().toISOString().split('T')[0]}.tar.gz`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully!');
      fetchAll();
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download backup');
    } finally {
      setDownloading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (s) => {
    if (s === 'success' || s === 'downloaded') return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> {s === 'downloaded' ? 'Downloaded' : 'Success'}</Badge>;
    if (s === 'failed') return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Failed</Badge>;
    if (s === 'running') return <Badge className="bg-blue-100 text-blue-800"><Clock className="w-3 h-3 mr-1" /> Running</Badge>;
    return <Badge variant="secondary">{s}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Database Backup</h1>
        <p className="text-gray-600 mt-1">Download database backup to your local computer</p>
      </div>

      {/* Download Backup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Download Database Backup
          </CardTitle>
          <CardDescription>
            Create and download a complete database backup file (.tar.gz)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              {status?.last_backup && (
                <div className="space-y-1">
                  <p className="text-sm text-gray-600">
                    Last backup: <span className="font-medium">{formatDateTime(status.last_backup.date)}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    File: <span className="font-medium">{status.last_backup.file_name}</span>
                    {status.last_backup.file_size_mb && (
                      <span className="ml-2 text-gray-500">({status.last_backup.file_size_mb} MB)</span>
                    )}
                  </p>
                </div>
              )}
              {!status?.last_backup && (
                <p className="text-sm text-gray-500">No backups yet. Click to create your first backup.</p>
              )}
            </div>
            <Button 
              onClick={handleDownloadBackup} 
              disabled={downloading}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="download-backup-btn"
            >
              {downloading ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Creating Backup...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Download Backup
                </>
              )}
            </Button>
          </div>
          
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> Download a backup daily and save it to a safe location (USB drive, external hard drive, or cloud storage like Google Drive/Dropbox).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Backup Logs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Backup History (Last 30 Days)
              </CardTitle>
              <CardDescription>
                {logs.stats?.total > 0 && (
                  <span>{logs.stats.success} successful downloads</span>
                )}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchLogs}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {logs.logs?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No backup history yet</p>
              <p className="text-sm mt-1">Download your first backup to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Downloaded By</TableHead>
                  <TableHead>Date & Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.logs?.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-medium">{log.file_name || '-'}</TableCell>
                    <TableCell>{getStatusBadge(log.status)}</TableCell>
                    <TableCell>{log.file_size_mb ? `${log.file_size_mb} MB` : '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {log.triggered_by?.replace('manual download (', '').replace(')', '') || 'Admin'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{formatDateTime(log.started_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BackupSettings;
